import type { TeamMemberAvailabilityVM } from '../availability/TeamMemberAvailabilityManager';
import type { EffectiveDayAvailability } from './routyContext';
import { formatTimeString } from '../../utils/timeUtils';

/**
 * Why a reschedule target is unusable, in the order Routy should say it:
 * day-level reasons first (day off / blocked window / outside hours), then
 * the busy slots in the window, then the daily cap.
 */
export type RescheduleBlockerKind =
  | 'dayOff'
  | 'outsideHours'
  | 'blockedWindow'
  | 'externalEvent'
  | 'appointment'
  | 'dailyCap'
  | 'unavailable';

export interface RescheduleBlocker {
  kind: RescheduleBlockerKind;
  message: string;
}

/** Day-level blockers - the only kinds the user can knowingly override (the backend does not enforce work hours on update). */
export const SOFT_RESCHEDULE_BLOCKER_KINDS: ReadonlySet<RescheduleBlockerKind> = new Set<RescheduleBlockerKind>([
  'dayOff',
  'outsideHours',
  'blockedWindow',
]);

export interface DescribeRescheduleBlockersArgs {
  /** The server-side availability check for one assignee (GetTeamMemberAvailability). */
  member: TeamMemberAvailabilityVM;
  /** The assignee's effective availability on the target date (getEffectiveDayAvailability). */
  effectiveDay: EffectiveDayAvailability;
  /** "You" for the operator, otherwise the member's name. */
  memberLabel: string;
  /** The target date, e.g. formatDateMedium(target). */
  dateLabel: string;
  /** The requested window in tenant-local HH:MM. */
  windowLocal: { start: string; end: string };
  /** False for duration-only changes (extend/shorten): the appointment stays on its current day. */
  checkWorkingHours: boolean;
  /** Formats a conflict's UTC window for display, e.g. "10:00 AM – 11:00 AM". */
  formatConflictWindow: (startUtc: string, endUtc: string) => string;
}

const isSelf = (memberLabel: string) => memberLabel === 'You';

/**
 * Turn the server availability signals for one assignee into plain-language
 * reasons the requested reschedule time cannot be used. Empty when the time is
 * free. The backend status is authoritative; the effective day only picks the
 * wording for NotWorking (it cannot tell a day off from an outside-hours window).
 * Conflicts are reported whatever the status, so a day off that also holds a
 * blocking calendar event or appointment yields both a day-level and a hard blocker.
 */
export function describeRescheduleBlockers(args: DescribeRescheduleBlockersArgs): RescheduleBlocker[] {
  const { member, effectiveDay, memberLabel, dateLabel, windowLocal, checkWorkingHours, formatConflictWindow } = args;
  const self = isSelf(memberLabel);
  const subjectIs = self ? 'You are' : `${memberLabel} is`;
  const subjectHas = self ? 'You have' : `${memberLabel} has`;
  const possessive = self ? 'your' : `${memberLabel}'s`;
  const object = self ? 'you' : memberLabel;
  const windowLabel = `${formatTimeString(windowLocal.start)}–${formatTimeString(windowLocal.end)}`;

  const blockers: RescheduleBlocker[] = [];

  if (member.status === 'NotWorking' && checkWorkingHours) {
    const blockedWindow = (effectiveDay.blockedWindows ?? []).find(
      (w) => windowLocal.start < w.end && windowLocal.end > w.start,
    );
    if (!effectiveDay.isWorkDay) {
      blockers.push({ kind: 'dayOff', message: `${subjectIs} off on ${dateLabel}.` });
    } else if (blockedWindow) {
      blockers.push({
        kind: 'blockedWindow',
        message:
          `${subjectHas} ${blockedWindow.reason} blocked ` +
          `${formatTimeString(blockedWindow.start)}–${formatTimeString(blockedWindow.end)} that day.`,
      });
    } else {
      const withinHours = windowLocal.start >= effectiveDay.start && windowLocal.end <= effectiveDay.end;
      blockers.push({
        kind: 'outsideHours',
        message: withinHours
          ? `${subjectIs} not working ${windowLabel} on ${dateLabel}.`
          : `${windowLabel} is outside ${possessive} working hours ` +
            `(${formatTimeString(effectiveDay.start)}–${formatTimeString(effectiveDay.end)}).`,
      });
    }
  }

  for (const conflict of member.conflicts) {
    const window = formatConflictWindow(conflict.startUtc, conflict.endUtc);
    if (conflict.source === 'ExternalCalendar') {
      blockers.push({
        kind: 'externalEvent',
        message: conflict.title
          ? `This time is already blocked by ${possessive} calendar event "${conflict.title}" (${window}).`
          : `This time is already blocked by an event on ${possessive} calendar (${window}).`,
      });
    } else {
      blockers.push({
        kind: 'appointment',
        message: conflict.title
          ? `This time overlaps ${possessive} appointment "${conflict.title}" (${window}), including travel buffer.`
          : `This time overlaps another of ${possessive} appointments (${window}), including travel buffer.`,
      });
    }
  }

  if (member.isDailyCapReached) {
    blockers.push({
      kind: 'dailyCap',
      message:
        `The maximum of ${member.dailyCapacity ?? member.appointmentCount} appointments for ${object} ` +
        `on ${dateLabel} has been reached.`,
    });
  }

  if (member.status === 'Unavailable' && member.conflicts.length === 0) {
    blockers.push({
      kind: 'unavailable',
      message: self ? 'This time is not available.' : `This time is not available for ${memberLabel}.`,
    });
  }

  return blockers;
}

/** True when every blocker is a day-level one the user may knowingly override. */
export function onlySoftRescheduleBlockers(blockers: RescheduleBlocker[]): boolean {
  return blockers.length > 0 && blockers.every((b) => SOFT_RESCHEDULE_BLOCKER_KINDS.has(b.kind));
}

/**
 * Card text when the user knowingly moves an appointment onto a day off / outside
 * working hours. Names only the assignees who are off (dayOffLabels), not every assignee.
 */
export function describeDayOffOverride(
  blockers: RescheduleBlocker[],
  dateLabel: string,
  dayOffLabels: string[],
): string {
  if (blockers.some((b) => b.kind === 'dayOff')) {
    const labels = dayOffLabels.map((l) => (isSelf(l) ? 'you' : l));
    const who = labels.length > 0 ? labels.join(' and ') : 'the assignee';
    return `${dateLabel} is a day off for ${who} — you're moving it there anyway`;
  }
  return `${blockers.map((b) => b.message).join(' ')} — you're moving it there anyway`;
}
